import React from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../redux/useDispatch";
import BookingDatePicker from "../common/components/OrderDataStep/BookingDatePicker";
import BookingTimeSelect from "../common/components/OrderDataStep/BookingTimeSelect";
import BookingDurationSelect from "../common/components/OrderDataStep/BookingDurationSelect";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import moment from "moment-timezone";
import {
  endsOnSkippedDay,
  getAvailableDurations,
  getAvailableTimeSlots,
} from "../util/orderDataStepHelper";
import {
  dateSelected,
  durationSelected,
  timeSelected,
} from "../../../redux/slice/bookingCreation/bookingCreationSlice";
import LoaderOverlay from "../../layout/components/LoaderOverlay";
import _ from "lodash";
import { useBookingWindow } from "../common/hooks/useBookingWindow";
import { useSelectedLocationTimeZone } from "../../../util/locationTimeZone";

const OrderDataStep = () => {
  const { t } = useTranslation();
  // The tenant's configured booking horizon for this use case — the picker
  // used to cap dates at a hardcoded 18 months, which contradicted the one
  // month the availability request already asked for. One month is the
  // default here, so the effective behaviour is unchanged.
  const bookingWindow = useBookingWindow("rental");
  const dispatch = useAppDispatch();
  // The station's clock (#3219): slots are keyed in the location's zone.
  const timeZone = useSelectedLocationTimeZone();
  const groupAvailabilities = useSelector(
    (state: RootState) => state.bookingCreation.availabilities
  );
  const orderData = useSelector(
    (state: RootState) => state.bookingCreation.orderData
  );
  const selectedGroup = useSelector(
    (state: RootState) => state.bookingCreation.selectedGroup
  );
  const tenantConfig = useSelector(
    (state: RootState) => state.tenantConfig.config
  );
  const isLoading = _.isEmpty(groupAvailabilities);

  const handleDateChange = (date: moment.Moment | null) => {
    dispatch(dateSelected({ date }));
  };

  const handleTimeChange = (time: string) => {
    dispatch(timeSelected({ time }));
  };

  let availableDates;
  if (groupAvailabilities) {
    availableDates = Object.keys(groupAvailabilities).filter((dateKey) => {
      const timeSlots = groupAvailabilities[dateKey];
      return Object.values(timeSlots).some((slot) =>
        // @ts-ignore
        Object.values(slot).some((value) => value > 0)
      );
    });
  }

  const availableTimeSlots =
    groupAvailabilities && orderData.date
      ? getAvailableTimeSlots(groupAvailabilities, orderData.date, timeZone)
      : [];

  // Helper function to check if end date falls on a day to skip
  const shouldSkipDuration = (duration: number): boolean => {
    if (!orderData.date || !orderData.time || !tenantConfig.skipEndingInDays || tenantConfig.skipEndingInDays.length === 0) {
      return false;
    }
    return endsOnSkippedDay(orderData.date, orderData.time, duration, tenantConfig.skipEndingInDays, timeZone);
  };

  const availableDurations =
    groupAvailabilities && orderData.date && orderData.time
      ? getAvailableDurations(
          groupAvailabilities,
          orderData.date,
          orderData.time,
          timeZone
        )
        .filter((duration) => duration <= 86400) // Filter out durations > 2 months (i.e. permanent bookings for segmenta)
        .filter((duration) => !shouldSkipDuration(duration)) // Filter out durations ending on skipEndingInDays
      : [];

  const enableTimeSelect = Boolean(groupAvailabilities && orderData.date);
  const enableDurationSelect = Boolean(
    groupAvailabilities && orderData.date && orderData.time !== ""
  );

  // Extract the price information
  const priceMap =
    // @ts-ignore
    selectedGroup?.priceCategoryInfo?.priceScale?.reduce((acc, curr) => {
      acc[curr?.durationInMinutes] = curr.price;
      return acc;
    }, {} as Record<number, number>) || {};

  return (
    <div className="w-full">
      {groupAvailabilities &&
        (_.isEmpty(groupAvailabilities) ? (
          <div className="pl-5">
            <p className="text-slate-900">
              {t("book_location.order_data.error.no_availabilities_for_group")}
            </p>
          </div>
        ) : (
          <>
            <div className="w-full flex flex-col mt-5 mx-auto">
              {availableDates && (
                <BookingDatePicker
                  value={orderData.date}
                  onChange={(date) => handleDateChange(date)}
                  shouldDisableDate={(date) => false}
                  availableDates={availableDates}
                  maxDate={bookingWindow.latest}
                  label={t("general.delivery_date")}
                />
              )}
            </div>
            <div className="w-full flex flex-col mt-3 mx-auto">
              <BookingTimeSelect
                availableSlots={availableTimeSlots}
                value={orderData.time}
                onChange={handleTimeChange}
                availableDurations={availableDurations}
                disabled={!enableTimeSelect}
                label={t("general.select")}
              />
            </div>
            <div className="w-full flex flex-col mt-3 mx-auto">
              <BookingDurationSelect
                value={orderData.duration}
                onChange={(duration) =>
                  dispatch(durationSelected({ duration }))
                }
                availableDurations={availableDurations}
                disabled={!enableDurationSelect}
                prices={priceMap}
                label={t("general.duration")}
              />
            </div>
          </>
        ))}
      <LoaderOverlay open={isLoading} />
    </div>
  );
};

export default OrderDataStep;
